import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthUserCompany } from '@durma-soft/gros-sdk';
import { useGetCompanyTrackLocationsQuery } from '@durma-soft/gros-sdk/dist/hooks/queries';

import { Alert } from '@/components/shared/alert';
import { Spinner } from '@/components/shared/spinner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/shared/shadcn-ui/select';

import {
  CompanyLocationFormData,
  SingleCompanyLocationFormData,
} from '@/types/companies';

interface TrackLocationsProps {
  formData: CompanyLocationFormData | SingleCompanyLocationFormData;
  setCompanyTrackLocationId: (id: number | null) => void;
}

export const TrackLocations = ({
  formData,
  setCompanyTrackLocationId,
}: TrackLocationsProps) => {
  const uid = useId();
  const { t } = useTranslation();
  const company = useAuthUserCompany();

  const { data: trackLocations, isLoading } = useGetCompanyTrackLocationsQuery({
    company_id: company.id,
  });

  if (isLoading) return <Spinner />;

  if (!isLoading && trackLocations?.length === 0)
    return (
      <Alert
        variant="destructive"
        title={t('common.no-results')}
        description={t('company.track-location.no-results-description')}
      />
    );

  return (
    <Select
      value={
        formData.company_track_location_id
          ? `${formData.company_track_location_id}`
          : undefined
      }
      onValueChange={(e) => {
        if (formData.location_type === 'track') {
          setCompanyTrackLocationId(+e);
        } else {
          setCompanyTrackLocationId(null);
        }
      }}
    >
      <SelectTrigger id={uid + '-menu-type'}>
        <SelectValue placeholder={t('company.track-location.choose')} />
      </SelectTrigger>
      <SelectContent>
        {trackLocations &&
          trackLocations.map(({ id, name }) => (
            <SelectItem key={id} value={`${id}`}>
              {name}
            </SelectItem>
          ))}
      </SelectContent>
    </Select>
  );
};
